import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Issue, IssuePriority } from '@/types'
import { useState, useEffect, useRef } from 'react'
import * as customFieldApi from '../services/api/customField'
import {
  MinusIcon,
  UserCircleIcon,
  EllipsisVerticalIcon,
  LinkIcon,
  ArchiveBoxIcon,
  TrashIcon,
  CalendarIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronDoubleUpIcon,
  ChevronDoubleDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { deleteIssue, archiveIssue, moveIssue } from '../store/slices/issueSlice'
import { fetchProjects } from '../store/slices/projectSlice'
import { usePermissions, Permission } from '../hooks/usePermissions'
import { useToast } from './ToastContainer'

interface IssueCardProps {
  issue: Issue
  isDragging?: boolean
  onClick?: () => void
  projectId?: string
}

const priorityConfig = {
  [IssuePriority.HIGHEST]: {
    icon: ChevronDoubleUpIcon,
    color: 'text-red-500',
    bg: 'bg-red-50',
  },
  [IssuePriority.HIGH]: {
    icon: ChevronUpIcon,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
  },
  [IssuePriority.MEDIUM]: {
    icon: MinusIcon,
    color: 'text-yellow-500',
    bg: 'bg-yellow-50',
  },
  [IssuePriority.LOW]: {
    icon: ChevronDownIcon,
    color: 'text-green-500',
    bg: 'bg-green-50',
  },
  [IssuePriority.LOWEST]: {
    icon: ChevronDoubleDownIcon,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
  },
}

// 計算到期日期的狀態
const getDueDateStatus = (dueDate: Date | string | null | undefined) => {
  if (!dueDate) return null
  
  const due = new Date(dueDate)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
  
  // 計算天數差異
  const diffTime = dueDay.getTime() - today.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  // 格式化日期顯示
  const dateText = due.toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' })
  
  if (diffDays < 0) {
    // 已過期
    return {
      status: 'overdue',
      borderClass: 'border-red-500',
      textClass: 'text-red-700',
      bgClass: 'bg-red-100',
      days: Math.abs(diffDays),
      text: `${dateText} (過期 ${Math.abs(diffDays)} 天)`
    }
  } else if (diffDays === 0) {
    // 今日到期
    return {
      status: 'today',
      borderClass: 'border-red-500',
      textClass: 'text-red-700',
      bgClass: 'bg-red-100',
      days: 0,
      text: `${dateText} (今日到期)`
    }
  } else if (diffDays <= 7) {
    // 7天內到期
    return {
      status: 'soon',
      borderClass: 'border-yellow-500',
      textClass: 'text-yellow-700',
      bgClass: 'bg-yellow-100',
      days: diffDays,
      text: `${dateText} (${diffDays} 天後到期)`
    }
  }
  
  // 超過7天，只顯示日期
  return {
    status: 'normal',
    borderClass: 'border-gray-300',
    textClass: 'text-gray-600',
    bgClass: 'bg-gray-50',
    days: diffDays,
    text: dateText
  }
}

const IssueCard = ({ issue, isDragging = false, onClick, projectId }: IssueCardProps) => {
  const [isPressed, setIsPressed] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [touchStartTime, setTouchStartTime] = useState(0)
  const [isDragStarted, setIsDragStarted] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [submenuDirection, setSubmenuDirection] = useState<'left' | 'right'>('right')
  const [showSubmenu, setShowSubmenu] = useState(false)
  const [projectCustomFields, setProjectCustomFields] = useState<customFieldApi.ProjectCustomField[]>([])
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const submenuTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()
  const { user, isAuthenticated } = useAppSelector((state) => state.auth)
  const { projects } = useAppSelector((state) => state.project)

  // 專案權限檢查
  const { hasPermission } = usePermissions(projectId)
  const canArchiveIssue = hasPermission(Permission.ARCHIVE_ISSUE)
  const canDeleteIssue = hasPermission(Permission.DELETE_ISSUE)
  const canEditIssue = hasPermission(Permission.EDIT_ISSUE)

  // 載入專案列表用於移動功能
  useEffect(() => {
    if (isAuthenticated && projects.length === 0) {
      dispatch(fetchProjects())
    }
  }, [dispatch, isAuthenticated, projects.length])

  // 獲取專案自訂欄位設定
  useEffect(() => {
    const fetchProjectCustomFields = async () => {
      try {
        const currentProjectId = projectId || issue.projectId
        if (currentProjectId) {
          const data = await customFieldApi.getProjectCustomFields(currentProjectId)
          setProjectCustomFields(data.enabledFields || [])
        }
      } catch (error) {
        console.error('獲取專案自訂欄位設定失敗:', error)
        setProjectCustomFields([])
      }
    }

    fetchProjectCustomFields()
  }, [projectId, issue.projectId])

  // 檢查子選單應該向哪個方向彈出
const checkSubmenuDirection = () => {
  if (!menuButtonRef.current) return

  const rect = menuButtonRef.current.getBoundingClientRect()
  const windowWidth = window.innerWidth

  // 使用更實用的邏輯：如果按鈕在螢幕左側 1/3 位置，向右彈出
  const leftThirdThreshold = windowWidth / 3

    // 如果按鈕在螢幕左側 1/3 位置，向右彈出，否則向左彈出
    if (rect.left < leftThirdThreshold) {
      setSubmenuDirection('right')
    } else {
      setSubmenuDirection('left')
    }
  }

  // 處理子選單顯示
  const handleSubmenuShow = () => {
    if (submenuTimeoutRef.current) {
      clearTimeout(submenuTimeoutRef.current)
      submenuTimeoutRef.current = null
    }
    checkSubmenuDirection()
    setShowSubmenu(true)
  }

  // 處理子選單隱藏
  const handleSubmenuHide = () => {
    submenuTimeoutRef.current = setTimeout(() => {
      setShowSubmenu(false)
    }, 300) // 300ms 延遲，給用戶時間移動到子選單
  }

  // 清理定時器
  useEffect(() => {
    return () => {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current)
      }
    }
  }, [])

  // 計算可移動到的專案
  const availableProjects = projects.filter(project => {
    if (!user) return false
    if (project.id === issue.projectId) return false // 不能移動到相同專案

    // Admin 和 Super Admin 可以移動到任何專案
    const isAdmin = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN'
    if (isAdmin) return true

    // 一般用戶需要檢查是否為該專案成員並有建立議題權限
    const membership = project.members?.find(member => member.userId === user.id)
    return (
      membership?.role?.rolePermissions?.some((permission) => permission.permission === 'CREATE_ISSUE') ??
      false
    )
  })

  // 獲取重要的自訂欄位值（根據 showInCard 設定過濾）
type ImportantField = { name: string; value: string; type: string }

const getImportantCustomFields = (): ImportantField[] => {
  if (!issue.customFieldValues) return []

  return issue.customFieldValues
    .map((cfv) => {
      if (!cfv.field) return null

        const projectField = projectCustomFields.find((pcf) => pcf.fieldId === cfv.fieldId)
        if (projectField?.showInCard === false) {
          return null
        }

        let displayValue = ''
        if (cfv.value) displayValue = cfv.value
        else if (cfv.numberValue !== null && cfv.numberValue !== undefined) displayValue = String(cfv.numberValue)
        else if (cfv.dateValue) {
          displayValue = new Date(cfv.dateValue).toLocaleDateString('zh-TW')
        }

        if (!displayValue) {
          return null
        }

        if (!cfv.field) {
          return null
        }

        return {
          name: cfv.field.name,
          value: displayValue,
          type: String(cfv.field.type),
        }
      })
      .filter((field): field is ImportantField => field !== null)
      .slice(0, 3)
}
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: issue.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isSortableDragging ? 'none' : transition,
  }

  const priority = priorityConfig[issue.priority]
  const PriorityIcon = priority.icon
  const dueDateStatus = getDueDateStatus(issue.dueDate)

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    // 只有在不拖拽時才觸發點擊
    if (!isSortableDragging && !isDragStarted && onClick) {
      e.stopPropagation()
      console.log('Card clicked:', issue.title)
      onClick()
    }
  }

  const handleTouchStart = () => {
    setTouchStartTime(Date.now())
    setIsPressed(true)
    
    // 300ms 後顯示拖拽提示
    setTimeout(() => {
      if (isPressed) {
        setShowTooltip(true)
      }
    }, 300)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const touchDuration = Date.now() - touchStartTime
    setIsPressed(false)
    setShowTooltip(false)

    // 如果觸摸時間少於 300ms 且沒有開始拖拽，當作點擊處理
    if (touchDuration < 300 && !isSortableDragging && !isDragStarted && onClick) {
      e.stopPropagation()
      onClick()
    }
    
    setIsDragStarted(false)
  }

  // 監聽拖拽狀態變化
  useEffect(() => {
    if (isSortableDragging) {
      setIsDragStarted(true)
      setIsPressed(false)
      setShowTooltip(false)
    }
  }, [isSortableDragging])

  // 處理複製分享連結
  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const url = `${window.location.origin}/issues/${issue.id}`
    try {
      await navigator.clipboard.writeText(url)
      showSuccess('已複製連結')
    } catch (error) {
      console.error('複製連結失敗:', error)
      showError('複製失敗', '無法複製連結到剪貼板')
    }
  }

  // 處理封存卡片
  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const isArchiving = !issue.archived
    const action = isArchiving ? '封存' : '取消封存'
    
    try {
      await dispatch(archiveIssue({ issueId: issue.id, archived: isArchiving })).unwrap()
      showSuccess(`已${action}議題`, `議題「${issue.title}」已${action}`)
    } catch (error) {
      console.error(`${action}議題失敗:`, error)
      showError(`${action}失敗`, `無法${action}議題`)
    }
  }

  // 處理刪除卡片
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await dispatch(deleteIssue(issue.id)).unwrap()
      showSuccess('已移至回收桶', `議題「${issue.title}」已移至回收桶`)
    } catch (error) {
      console.error('刪除議題失敗:', error)
      showError('刪除失敗', '無法刪除議題')
    }
  }

  // 處理移動議題到其他專案
  const handleMoveToProject = async (targetProjectId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    const targetProject = projects.find(p => p.id === targetProjectId)
    if (!targetProject) return

    try {
      const result = await dispatch(moveIssue({
        issueId: issue.id,
        targetProjectId
      })).unwrap() as { issue?: Issue; data?: { issue?: Issue } }

      const movedIssue = result.issue ?? result.data?.issue ?? null

      if (!movedIssue) {
        showError('移動失敗', '無法取得更新後議題資料，請稍後再試')
        return
      }

      showSuccess(
        '議題已成功移動',
        `議題「${issue.title}」已移動到專案「${targetProject.name}」，新編號：${movedIssue.key}`
      )
    } catch (error) {
      console.error('移動議題失敗:', error)
      showError('移動失敗', '無法移動議題到目標專案')
    }
  }

  if (isDragging) {
    return (
      <div className="card p-4 opacity-50 rotate-5 cursor-grabbing no-select">
        <div className="flex items-start justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900 line-clamp-2 flex-1 mr-2">
            {issue.title}
          </h4>
          <div className={`p-1 rounded ${priority.bg} flex-shrink-0`}>
            <PriorityIcon className={`h-3 w-3 ${priority.color}`} />
          </div>
        </div>
        
        {/* 到期日期顯示 */}
        {dueDateStatus && (
          <div className={`flex items-center space-x-1 mb-2 px-2 py-1 rounded border ${dueDateStatus.bgClass} ${dueDateStatus.borderClass} text-xs`}>
            <CalendarIcon className={`h-3 w-3 ${dueDateStatus.textClass}`} />
            <span className={dueDateStatus.textClass}>{dueDateStatus.text}</span>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500">{issue.key}</span>
          {issue.assignee ? (
            issue.assignee.avatar ? (
              <img
                src={issue.assignee.avatar}
                alt={issue.assignee.name}
                className="w-6 h-6 rounded-full"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
                <span className="text-xs font-medium text-gray-600">
                  {issue.assignee.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )
          ) : (
            <UserCircleIcon className="w-6 h-6 text-gray-400" />
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative card p-4 cursor-grab hover:shadow-md transition-all duration-200 ${
        isSortableDragging ? 'opacity-50' : ''
      } ${isPressed ? 'scale-105 shadow-lg' : ''} ${onClick ? 'hover:cursor-pointer' : ''}`}
    >
      {/* 觸控提示 */}
      {showTooltip && (
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10 md:hidden">
          長按拖拽
        </div>
      )}
      <div className="flex items-start justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 flex-1 mr-2">
          {issue.title}
        </h4>
        <div className="flex items-center space-x-1 flex-shrink-0">
          <div className={`p-1 rounded ${priority.bg}`}>
            <PriorityIcon className={`h-3 w-3 ${priority.color}`} />
          </div>
          {/* 三點選單 - 只在 hover 時顯示 */}
          {isHovered && (
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded hover:bg-gray-100 transition-colors duration-150"
              >
                <EllipsisVerticalIcon className="h-3 w-3 text-gray-500" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-20 w-40 mt-1 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    {/* 複製分享連結 - 所有人都可以使用 */}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleCopyLink}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } group flex items-center w-full px-3 py-2 text-sm text-gray-700`}
                        >
                          <LinkIcon className="w-4 h-4 mr-2 text-gray-500" />
                          複製分享連結
                        </button>
                      )}
                    </Menu.Item>
                    
                    {/* 移動到其他專案 - 需要編輯權限 */}
                    {canEditIssue && (
                      <Menu.Item>
                        {({ active }) => (
                          <div
                            className="relative"
                            onMouseEnter={handleSubmenuShow}
                            onMouseLeave={handleSubmenuHide}
                          >
                            <button
                              ref={menuButtonRef}
                              className={`${
                                active ? 'bg-gray-100' : ''
                              } flex items-center w-full px-3 py-2 text-sm text-gray-700 justify-between`}
                            >
                              <span className="flex items-center">
                                {submenuDirection === 'left' ? (
                                  <ArrowLeftIcon className="w-4 h-4 mr-2 text-gray-500" />
                                ) : (
                                  <ArrowRightIcon className="w-4 h-4 mr-2 text-gray-500" />
                                )}
                                移動到其他專案
                              </span>
                              {submenuDirection === 'left' ? (
                                <ChevronLeftIcon className="w-3 h-3 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="w-3 h-3 text-gray-400" />
                              )}
                            </button>

                            {/* 浮動子選單 - 動態定位 */}
                            {showSubmenu && (
                              <div
                                className={`absolute top-0 z-[9999] ${
                                  submenuDirection === 'left'
                                    ? 'right-full mr-1'
                                    : 'left-full ml-1'
                                }`}
                                onMouseEnter={handleSubmenuShow}
                                onMouseLeave={handleSubmenuHide}
                              >
                              <div className="bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 min-w-[200px] max-h-60 overflow-y-auto">
                                {availableProjects.length > 0 ? (
                                  availableProjects.map(project => (
                                    <button
                                      key={project.id}
                                      onClick={(e) => handleMoveToProject(project.id, e)}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                      title={`移動到專案: ${project.name}`}
                                    >
                                      <div className="truncate">{project.name}</div>
                                      <div className="text-xs text-gray-500 truncate">{project.key}</div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-3 text-sm text-gray-500 italic">
                                    缺少其他專案權限
                                  </div>
                                )}
                              </div>
                              </div>
                            )}
                          </div>
                        )}
                      </Menu.Item>
                    )}

                    {/* 封存卡片 - 需要權限 */}
                    {canArchiveIssue && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleArchive}
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } group flex items-center w-full px-3 py-2 text-sm text-gray-700`}
                          >
                            <ArchiveBoxIcon className="w-4 h-4 mr-2 text-gray-500" />
                            {issue.archived ? '取消封存' : '封存卡片'}
                          </button>
                        )}
                      </Menu.Item>
                    )}
                    
                    {/* 刪除卡片 - 需要權限 */}
                    {canDeleteIssue && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleDelete}
                            className={`${
                              active ? 'bg-red-50' : ''
                            } group flex items-center w-full px-3 py-2 text-sm text-red-600`}
                          >
                            <TrashIcon className="w-4 h-4 mr-2 text-red-500" />
                            刪除卡片
                          </button>
                        )}
                      </Menu.Item>
                    )}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          )}
        </div>
      </div>

      {issue.description && (
        <div 
          className="text-xs text-gray-600 mb-3 line-clamp-2 prose prose-xs max-w-none"
          dangerouslySetInnerHTML={{ 
            __html: issue.description
              .replace(/<img[^>]*>/g, '[圖片]') // 將圖片替換為文字標示
              .replace(/<[^>]*>/g, '') // 移除其他 HTML 標籤
              .substring(0, 100) // 限制長度
          }}
        />
      )}

      {/* 到期日期顯示 */}
      {dueDateStatus && (
        <div className={`flex items-center space-x-1 mb-3 px-2 py-1 rounded border ${dueDateStatus.bgClass} ${dueDateStatus.borderClass} text-xs`}>
          <CalendarIcon className={`h-3 w-3 ${dueDateStatus.textClass}`} />
          <span className={dueDateStatus.textClass}>{dueDateStatus.text}</span>
        </div>
      )}

      {/* Labels 顯示區域 */}
      {issue.labels && issue.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {issue.labels.map((label) => (
            <span
              key={label.id}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
              style={{
                backgroundColor: label.color + '20',
                color: label.color,
              }}
            >
              {label.name}
            </span>
          ))}
        </div>
      )}

      {/* 自訂欄位顯示 */}
      {(() => {
        const customFields = getImportantCustomFields()
        return customFields.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {customFields.map((field, index) => (
                <div
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded border max-w-[120px]"
                  title={`${field.name}: ${field.value}`}
                >
                  <span className="font-medium text-gray-600 mr-1 truncate">{field.name}:</span>
                  <span className="truncate">{field.value}</span>
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500">{issue.key}</span>

        {issue.assignee ? (
          issue.assignee.avatar ? (
            <img
              src={issue.assignee.avatar}
              alt={issue.assignee.name}
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-xs font-medium text-gray-600">
                {issue.assignee.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )
        ) : (
          <UserCircleIcon className="w-6 h-6 text-gray-400" />
        )}
      </div>
    </div>
  )
}

export default IssueCard
